import { useGetFeatures } from 'App/actions/api/features';
import features, { FOUNDATION } from 'config/features';
import { createContext, useContext, useMemo } from 'react';
import { defineMessage } from 'react-intl';

export const FEATURE_TOGGLES = {
  FLOW_VISUALISATION: 'flowVisualisation',
  FLOW_CONTROL: 'flowControl',
  FLOW_SPEC_EDIT: 'flowSpecEdit',
  RESUBMIT_TRACES: 'resubmitTraces',
  MULTI_SITE: 'multiSite',
  ALERTS: 'alerts',
  REPORTING: 'reporting',
  THEMING: 'theming',
  MICROSOFT_LOGIN: 'microsoftLogin',
  BEATBOX: 'beatbox',
  TASKS: 'tasks',
  METRICS_GRAPH: 'metricsGraph',
  FLOW_TRACES_V2: 'flowTracesV2',
  FOUNDATION: 'foundation',
  NOT_FOUNDATION: 'notFoundation',
  FLOW_TRACES_FORWARDING_PATH: 'flowTracesForwardingPath',
  CONNECTIVITY_CHECK: 'connectivityCheck',
  AGENT: 'connectAgent',
};

export const FEATURE_DEFINITIONS = [
  {
    key_id: FEATURE_TOGGLES.ALERTS,
    nameIntl: defineMessage({ id: 'FeatureToggle-definition-alerts-name', defaultMessage: 'Alerts' }),
    descriptionIntl: defineMessage({
      id: 'FeatureToggle-definition-alerts-description',
      defaultMessage: 'Alerts page in Heartbeat',
    }),
  },
  {
    key_id: FEATURE_TOGGLES.BEATBOX,
    nameIntl: defineMessage({ id: 'FeatureToggle-definition-beatbox-name', defaultMessage: 'Beatbox' }),
    descriptionIntl: defineMessage({
      id: 'FeatureToggle-definition-beatbox-description',
      defaultMessage: 'Beatbox page in Heartbeat',
    }),
  },
  {
    key_id: FEATURE_TOGGLES.TASKS,
    nameIntl: defineMessage({ id: 'FeatureToggle-definition-tasks-name', defaultMessage: 'Tasks' }),
    descriptionIntl: defineMessage({
      id: 'FeatureToggle-definition-tasks-description',
      defaultMessage: 'Tasks in the filter bar',
    }),
  },
  {
    key_id: FEATURE_TOGGLES.FLOW_CONTROL,
    nameIntl: defineMessage({ id: 'FeatureToggle-definition-flowControl-name', defaultMessage: 'Flow control' }),
    descriptionIntl: defineMessage({
      id: 'FeatureToggle-definition-flowControl-description',
      defaultMessage: 'Add capabilities for pausing and starting flows',
    }),
  },
  {
    key_id: FEATURE_TOGGLES.FLOW_SPEC_EDIT,
    nameIntl: defineMessage({ id: 'FeatureToggle-definition-flowSpecEdit-name', defaultMessage: 'FlowSpec Edit' }),
    descriptionIntl: defineMessage({
      id: 'FeatureToggle-definition-flowSpecEdit-description',
      defaultMessage: 'Add possibility to edit flowSpec',
    }),
  },
  {
    key_id: FEATURE_TOGGLES.RESUBMIT_TRACES,
    nameIntl: defineMessage({ id: 'FeatureToggle-definition-resubmitTraces-name', defaultMessage: 'Resubmit traces' }),
    descriptionIntl: defineMessage({
      id: 'FeatureToggle-definition-resubmitTraces-description',
      defaultMessage: 'Turn on resubmit in flow traces',
    }),
  },
];

const defaultFeatures = {
  flowVisualisation: false,
  flowControl: false,
  flowSpecEdit: false,
  resubmitTraces: false,
  alerts: false,
  reporting: false,
  theming: false,
  microsoftLogin: false,
  beatbox: false,
  tasks: false,
  ghostwriterDemo: false,
  metricsGraph: false,
  foundation: FOUNDATION || false,
  notFoundation: !FOUNDATION,
  flowTracesForwardingPath: false,
  connectivityCheck: false,
  connectAgent: false,
  flowVisualisationDeprecated: false,
};

export const FeatureContext = createContext(null);

export const useFeatureContext = () => {
  const featureContext = useContext(FeatureContext);
  if (!featureContext) {
    return {
      features: {
        ...defaultFeatures,
        ...features,
      },
      initialized: false,
    };
  }
  return featureContext;
};

const FeatureProvider = ({ children }) => {
  const { data: featuresData, isSuccess, isError, isLoading } = useGetFeatures();
  const featuresContextData = useMemo(() => {
    const compiledFeatures = {
      ...defaultFeatures,
      ...features,
      ...(featuresData || {}),
    };
    return {
      features: compiledFeatures,
      initialized: FOUNDATION || ((isSuccess || isError) && !isLoading),
      isError,
      isSuccess,
    };
  }, [featuresData, isSuccess, isError, isLoading]);

  return <FeatureContext.Provider value={featuresContextData}>{children}</FeatureContext.Provider>;
};

export default FeatureProvider;
